/**
 * Shown to an applicant whose account was flagged for an inappropriate upload
 * (profiles.tags contains "flagged:inappropriate-upload", set by the OCR check).
 * Gives them a direct way to contest it through the portal message box.
 * Re-checks when PassportStep dispatches "te:account-flagged".
 */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ShieldAlert } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { usePortalMessages } from "@/components/portal/PortalMessages";

export const ACCOUNT_FLAG_TAG = "flagged:inappropriate-upload";
export const ACCOUNT_FLAGGED_EVENT = "te:account-flagged";

export default function AccountFlagNotice() {
  const { t } = useTranslation("portal");
  const { user } = useAuth();
  const messages = usePortalMessages();
  const [flagged, setFlagged] = useState(false);

  const check = useCallback(async () => {
    if (!user) return;
    // profiles.tags predates the generated types (see types.ts drift).
    const { data } = await supabase.from("profiles").select("tags" as never).eq("id", user.id).maybeSingle();
    const tags = (data as unknown as { tags?: string[] } | null)?.tags;
    setFlagged(Array.isArray(tags) && tags.includes(ACCOUNT_FLAG_TAG));
  }, [user]);

  useEffect(() => {
    void check();
    const onFlag = () => { setFlagged(true); void check(); };
    window.addEventListener(ACCOUNT_FLAGGED_EVENT, onFlag);
    return () => window.removeEventListener(ACCOUNT_FLAGGED_EVENT, onFlag);
  }, [check]);

  if (!flagged) return null;
  return (
    <div role="alert" className="mx-auto mt-3 max-w-5xl rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:bg-red-950/30 dark:text-red-100">
      <div className="flex items-start gap-3">
        <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
        <div className="flex-1 space-y-2">
          <p className="font-semibold">{t("review.validation.accountFlaggedTitle")}</p>
          <p>{t("review.validation.uploadFlagged")}</p>
          {messages && (
            <Button size="sm" variant="outline" className="border-red-300 bg-white text-red-800 hover:bg-red-100" onClick={() => messages.openMessages()}>
              {t("review.validation.contactUs")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
